import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { MdLocationCity, MdMonitorHeart } from 'react-icons/md';
import { toast } from 'react-toastify';
import AdminHeader from '../../components/AdminHeader';
import LayoutContainer from '../../components/LayoutContainer';
import elementItemService from '../../services/elementItemService';

const POLL_OPTIONS = [
  { value: 10000, label: '10 sec' },
  { value: 15000, label: '15 sec' },
  { value: 30000, label: '30 sec' },
  { value: 60000, label: '1 min' },
];

const STATUS_TONE = {
  verified: {
    bg: '#ecfdf3',
    color: '#067647',
    border: '#abefc6',
    track: '#d1fadf',
    label: 'Verified',
  },
  discrepancy: {
    bg: '#fef3f2',
    color: '#b42318',
    border: '#fecdca',
    track: '#fee4e2',
    label: 'Discrepancy',
  },
  exceeded: {
    bg: '#fffaeb',
    color: '#b54708',
    border: '#fedf89',
    track: '#fef0c7',
    label: 'Exceeded',
  },
  pending: {
    bg: '#f8fafc',
    color: '#475467',
    border: '#e4e7ec',
    track: '#eef2f6',
    label: 'Pending',
  },
};

const ACTIVITY_META = {
  added: {
    bg: '#ecfdf3',
    color: '#067647',
    border: '#abefc6',
    icon: AddCircleOutlineIcon,
    label: 'Added',
  },
  removed: {
    bg: '#fef3f2',
    color: '#b42318',
    border: '#fecdca',
    icon: RemoveCircleOutlineIcon,
    label: 'Removed',
  },
  status: {
    bg: '#eff8ff',
    color: '#175cd3',
    border: '#b2ddff',
    icon: SyncAltIcon,
    label: 'Status',
  },
  modified: {
    bg: '#fffaeb',
    color: '#b54708',
    border: '#fedf89',
    icon: EditOutlinedIcon,
    label: 'Edited',
  },
};

const filterFieldSx = {
  minWidth: { xs: '100%', sm: 260 },
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    backgroundColor: '#fff',
    fontFamily: 'Roboto, sans-serif',
    minHeight: 42,
    '& fieldset': { borderColor: '#efc4bd' },
    '&:hover fieldset': { borderColor: '#be210b' },
    '&.Mui-focused fieldset': { borderColor: '#be210b', borderWidth: 1.5 },
  },
  '& .MuiSelect-select': {
    fontFamily: 'Roboto, sans-serif',
    fontSize: '0.875rem',
    fontWeight: 600,
    textTransform: 'none',
    py: 1.1,
    pr: '36px !important',
  },
};

const locationMenuProps = {
  disableScrollLock: true,
  anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
  transformOrigin: { vertical: 'top', horizontal: 'left' },
  PaperProps: {
    sx: {
      mt: 0.75,
      minWidth: 260,
      maxWidth: 360,
      maxHeight: 320,
      borderRadius: '12px',
      border: '1px solid #f0d5d1',
      boxShadow: '0 16px 36px rgba(47, 53, 66, 0.14)',
      overflow: 'hidden',
      '& .MuiList-root': {
        py: 0.75,
        px: 0.75,
      },
      '& .MuiMenuItem-root': {
        fontFamily: 'Roboto, sans-serif',
        fontSize: '0.85rem',
        fontWeight: 600,
        textTransform: 'none',
        borderRadius: '8px',
        minHeight: 40,
        px: 1.5,
        py: 1,
        color: '#2F3542',
        whiteSpace: 'normal',
        lineHeight: 1.35,
        '&:hover': {
          backgroundColor: '#fff1ef',
          color: '#be210b',
        },
        '&.Mui-selected': {
          backgroundColor: '#fff1ef',
          color: '#be210b',
          '&:hover': {
            backgroundColor: '#ffe4df',
          },
        },
      },
    },
  },
};

const fingerprint = (item) =>
  [
    item.particulars,
    item.dimensions,
    item.quantity,
    item.unit,
    item.size,
    item.status,
    item.remarks,
    item.statusUpdatedBy,
  ].join('|');

const statusLabel = (value) => STATUS_TONE[value]?.label || 'Pending';

const timeLabel = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
};

const EmptyValue = () => (
  <Box
    component='span'
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      px: 1,
      py: 0.2,
      borderRadius: '999px',
      backgroundColor: '#f2f4f7',
      color: '#98a2b3',
      fontSize: '0.72rem',
      fontWeight: 600,
      letterSpacing: '0.02em',
    }}
  >
    Not set
  </Box>
);

const SectionCard = ({ title, subtitle, action, children, bodySx }) => (
  <Paper
    elevation={0}
    sx={{
      border: '1px solid #f0d5d1',
      borderRadius: '14px',
      overflow: 'hidden',
      backgroundColor: '#fff',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
    }}
  >
    <Box
      sx={{
        px: 2,
        py: 1.5,
        backgroundColor: '#fff7f6',
        borderBottom: '1px solid #f3e8e6',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
        flexWrap: 'wrap',
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontWeight: 700, color: '#2F3542', fontSize: '0.95rem' }}>
          {title}
        </Typography>
        {subtitle ? (
          <Typography sx={{ color: '#667085', fontSize: '0.78rem', mt: 0.2 }}>
            {subtitle}
          </Typography>
        ) : null}
      </Box>
      {action}
    </Box>
    <Box sx={{ flex: 1, minHeight: 0, ...bodySx }}>{children}</Box>
  </Paper>
);

const MetricTile = ({ label, value, tone, percent }) => (
  <Box
    sx={{
      p: 1.6,
      borderRadius: '12px',
      border: `1px solid ${tone.border}`,
      background: `linear-gradient(180deg, ${tone.bg} 0%, #ffffff 100%)`,
      minWidth: 0,
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.8 }}>
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          backgroundColor: tone.color,
          flexShrink: 0,
        }}
      />
      <Typography
        sx={{
          fontSize: '0.68rem',
          fontWeight: 700,
          letterSpacing: '0.05em',
          color: tone.color,
          textTransform: 'uppercase',
        }}
      >
        {label}
      </Typography>
    </Box>
    <Typography sx={{ fontSize: '1.55rem', fontWeight: 760, color: '#101828', lineHeight: 1 }}>
      {value}
    </Typography>
    {typeof percent === 'number' ? (
      <Box sx={{ mt: 1.1 }}>
        <LinearProgress
          variant='determinate'
          value={percent}
          sx={{
            height: 6,
            borderRadius: 99,
            backgroundColor: tone.track,
            '& .MuiLinearProgress-bar': {
              borderRadius: 99,
              backgroundColor: tone.color,
            },
          }}
        />
        <Typography sx={{ mt: 0.55, fontSize: '0.72rem', color: '#667085', fontWeight: 600 }}>
          {percent}% of items
        </Typography>
      </Box>
    ) : null}
  </Box>
);

const StructureMonitor = () => {
  const isMobile = useMediaQuery('(max-width:799px)', { noSsr: true });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [polling, setPolling] = useState(true);
  const [intervalMs, setIntervalMs] = useState(15000);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState('ALL');
  const [locationTotals, setLocationTotals] = useState([]);
  const [items, setItems] = useState([]);
  const [activity, setActivity] = useState([]);
  const previousMap = useRef(new Map());
  const firstLoad = useRef(true);

  const appendActivity = useCallback((entries) => {
    if (!entries.length) return;
    setActivity((current) => [...entries, ...current].slice(0, 120));
  }, []);

  const detectChanges = useCallback(
    (nextItems) => {
      const nextMap = new Map(nextItems.map((item) => [String(item.id), item]));
      const prevMap = previousMap.current;
      const now = new Date().toISOString();
      const events = [];

      if (!firstLoad.current) {
        nextMap.forEach((item, id) => {
          const previous = prevMap.get(id);
          if (!previous) {
            events.push({
              id: `${id}-added-${now}`,
              type: 'added',
              title: 'Item added',
              detail: item.particulars || 'Untitled item',
              meta: `${item.location} · ${item.sectionCode || '-'}`,
              at: now,
            });
            return;
          }
          if (previous.status !== item.status) {
            events.push({
              id: `${id}-status-${now}`,
              type: 'status',
              title: 'Status updated',
              detail: `${item.particulars || 'Item'}: ${statusLabel(previous.status)} → ${statusLabel(item.status)}`,
              meta: item.statusUpdatedBy
                ? `By ${item.statusUpdatedBy} · ${item.location}`
                : item.location,
              at: now,
            });
          } else if (fingerprint(previous) !== fingerprint(item)) {
            events.push({
              id: `${id}-modified-${now}`,
              type: 'modified',
              title: 'Item modified',
              detail: item.particulars || 'Untitled item',
              meta: `${item.location} · ${item.sectionCode || '-'}`,
              at: now,
            });
          }
        });

        prevMap.forEach((item, id) => {
          if (!nextMap.has(id)) {
            events.push({
              id: `${id}-removed-${now}`,
              type: 'removed',
              title: 'Item removed',
              detail: item.particulars || 'Untitled item',
              meta: `${item.location} · ${item.sectionCode || '-'}`,
              at: now,
            });
          }
        });
      }

      previousMap.current = nextMap;
      firstLoad.current = false;
      appendActivity(events);
    },
    [appendActivity],
  );

  const fetchMonitor = useCallback(
    async ({ silent } = {}) => {
      const eventId = localStorage.getItem('event_id');
      if (!eventId) {
        setItems([]);
        setLocationTotals([]);
        setLoading(false);
        return;
      }

      if (silent) setRefreshing(true);
      else setLoading(true);

      try {
        const [locationsResponse, itemsResponse] = await Promise.all([
          elementItemService.getElementLocations(eventId),
          elementItemService.getElementItemsByEvent(eventId),
        ]);
        const totals = locationsResponse.data?.locations || [];
        const flattened = elementItemService.flattenMonitorItems(itemsResponse.data);
        setLocationTotals(totals);
        setItems(flattened.items);
        detectChanges(flattened.items);
        setLastRefresh(new Date());
      } catch (error) {
        toast.error(
          error?.response?.data?.error ||
            error?.response?.data?.message ||
            'Could not load monitoring data',
        );
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [detectChanges],
  );

  useEffect(() => {
    fetchMonitor();
  }, [fetchMonitor]);

  useEffect(() => {
    if (!polling) return undefined;
    const timer = window.setInterval(() => {
      fetchMonitor({ silent: true });
    }, intervalMs);
    return () => window.clearInterval(timer);
  }, [fetchMonitor, intervalMs, polling]);

  const filteredItems = useMemo(() => {
    if (selectedLocation === 'ALL') return items;
    return items.filter((item) => item.location === selectedLocation);
  }, [items, selectedLocation]);

  const stats = useMemo(() => {
    const total = filteredItems.length;
    const verified = filteredItems.filter((item) => item.status === 'verified').length;
    const discrepancy = filteredItems.filter((item) => item.status === 'discrepancy').length;
    const exceeded = filteredItems.filter((item) => item.status === 'exceeded').length;
    const pending = total - verified - discrepancy - exceeded;
    const reviewed = verified + discrepancy + exceeded;
    const share = (count) => (total ? Math.round((count / total) * 100) : 0);
    return {
      total,
      verified,
      discrepancy,
      exceeded,
      pending,
      reviewed,
      progress: share(reviewed),
      share,
    };
  }, [filteredItems]);

  const uploadRows = useMemo(() => {
    const counts = {};
    items.forEach((item) => {
      counts[item.location] = (counts[item.location] || 0) + 1;
    });
    const names = new Set([
      ...locationTotals.map((item) => item.location),
      ...Object.keys(counts),
    ]);
    const rows = [...names]
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b))
      .map((location) => {
        const listed = locationTotals.find((item) => item.location === location);
        const itemCount = counts[location] || 0;
        return {
          location,
          listedTotal: listed?.total ?? itemCount,
          itemCount,
          status: itemCount > 0 ? 'Uploaded' : 'Empty',
        };
      });
    const maxCount = Math.max(...rows.map((row) => row.itemCount), 1);
    return rows.map((row) => ({
      ...row,
      fill: Math.round((row.itemCount / maxCount) * 100),
    }));
  }, [items, locationTotals]);

  const recentModifications = useMemo(() => {
    return [...filteredItems]
      .filter((item) => item.status || item.statusUpdatedBy || item.updatedAt)
      .sort((a, b) => {
        const aTime = new Date(a.updatedAt || 0).getTime();
        const bTime = new Date(b.updatedAt || 0).getTime();
        if (aTime || bTime) return bTime - aTime;
        return String(b.statusUpdatedBy || '').localeCompare(String(a.statusUpdatedBy || ''));
      })
      .slice(0, 40);
  }, [filteredItems]);

  const locationOptions = useMemo(() => {
    const names = new Set(uploadRows.map((row) => row.location));
    return ['ALL', ...[...names].sort((a, b) => a.localeCompare(b))];
  }, [uploadRows]);

  const uploadedCount = uploadRows.filter((row) => row.status === 'Uploaded').length;

  return (
    <LayoutContainer>
      <AdminHeader title='STRUCTURE MONITOR' />
      <div
        className='page-content-wrapper scrollable'
        style={{ width: '100%', minWidth: 0, boxSizing: 'border-box', padding: '8px' }}
      >
        <Box
          sx={{
            m: { xs: 0, md: 1 },
            display: 'flex',
            flexDirection: 'column',
            gap: { xs: 1.1, md: 1.5 },
          }}
        >
          <Paper
            elevation={0}
            sx={{
              p: { xs: 1.5, md: 2 },
              borderRadius: '16px',
              border: '1px solid #f3d4cf',
              background: 'linear-gradient(135deg, #ffffff 0%, #fff7f6 55%, #fff1ef 100%)',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1.5,
                alignItems: 'flex-start',
                justifyContent: 'space-between',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.4, minWidth: 0, flex: 1 }}>
                <Box
                  sx={{
                    width: { xs: 44, md: 54 },
                    height: { xs: 44, md: 54 },
                    borderRadius: '14px',
                    display: { xs: 'none', sm: 'grid' },
                    placeItems: 'center',
                    backgroundColor: '#fff',
                    border: '1px solid #f0d5d1',
                    color: '#be210b',
                    flexShrink: 0,
                    boxShadow: '0 8px 18px rgba(190, 33, 11, 0.08)',
                  }}
                >
                  <MdMonitorHeart size={24} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontWeight: 800,
                      color: '#2F3542',
                      fontSize: { xs: '1.05rem', md: '1.2rem' },
                      letterSpacing: '-0.01em',
                    }}
                  >
                    Real-time structure monitor
                  </Typography>
                  <Typography sx={{ color: '#667085', fontSize: '0.84rem', mt: 0.35 }}>
                    Track uploads, live edits, and verification progress across locations.
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1 }}>
                    {/* <Chip
                      size='small'
                      label={polling ? 'Live polling' : 'Paused'}
                      sx={{
                        height: 24,
                        fontWeight: 700,
                        backgroundColor: polling ? '#ecfdf3' : '#f2f4f7',
                        color: polling ? '#067647' : '#475467',
                        border: polling ? '1px solid #abefc6' : '1px solid #e4e7ec',
                      }}
                    /> */}
                    {/* <Chip
                      size='small'
                      label={
                        lastRefresh
                          ? `Updated ${lastRefresh.toLocaleTimeString()}`
                          : 'Waiting for first sync'
                      }
                      sx={{
                        height: 24,
                        fontWeight: 600,
                        backgroundColor: '#fff',
                        border: '1px solid #efc4bd',
                        color: '#2F3542',
                      }}
                    /> */}
                  </Box>
                </Box>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1,
                  alignItems: 'center',
                  justifyContent: { xs: 'stretch', md: 'flex-end' },
                  width: { xs: '100%', md: 'auto' },
                }}
              >
                <FormControl size='small' sx={filterFieldSx}>
                  <Select
                    value={selectedLocation}
                    displayEmpty
                    onChange={(event) => setSelectedLocation(event.target.value)}
                    MenuProps={locationMenuProps}
                    renderValue={(value) =>
                      value === 'ALL' ? 'All locations'?.toUpperCase() : value?.toUpperCase()
                    }
                    startAdornment={
                      <InputAdornment position='start' sx={{ ml: 0.75, mr: 0.25 }}>
                        <MdLocationCity color='#be210b' size={17} />
                      </InputAdornment>
                    }
                  >
                    {locationOptions.map((location) => (
                      <MenuItem key={location} value={location}>
                        {location === 'ALL' ? 'All locations'?.toUpperCase() : location?.toUpperCase()}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* <FormControl size='small' sx={{ ...filterFieldSx, minWidth: { xs: '48%', sm: 120 } }}>
                  <Select
                    value={intervalMs}
                    onChange={(event) => setIntervalMs(Number(event.target.value))}
                  >
                    {POLL_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl> */}

                {/* <IconButton
                  onClick={() => setPolling((value) => !value)}
                  sx={{
                    border: '1px solid #efc4bd',
                    borderRadius: 2,
                    backgroundColor: '#fff',
                    color: '#be210b',
                    '&:hover': { backgroundColor: '#fff1ef' },
                  }}
                  aria-label={polling ? 'Pause auto refresh' : 'Resume auto refresh'}
                >
                  {polling ? <PauseIcon /> : <PlayArrowIcon />}
                </IconButton> */}

                {/* <Button
                  variant='contained'
                  startIcon={
                    refreshing ? (
                      <CircularProgress size={16} sx={{ color: '#fff' }} />
                    ) : (
                      <RefreshIcon />
                    )
                  }
                  onClick={() => fetchMonitor({ silent: true })}
                  disabled={refreshing}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 700,
                    backgroundColor: '#be210b',
                    borderRadius: 2,
                    px: 2,
                    boxShadow: 'none',
                    flex: { xs: 1, sm: 'none' },
                    '&:hover': { backgroundColor: '#a61c09', boxShadow: 'none' },
                  }}
                >
                  Refresh
                </Button> */}
              </Box>
            </Box>

            <Box
              sx={{
                mt: 2,
                p: 1.5,
                borderRadius: '12px',
                backgroundColor: '#fff',
                border: '1px solid #f3e8e6',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 1,
                  mb: 1,
                  flexWrap: 'wrap',
                }}
              >
                <Typography sx={{ fontWeight: 700, color: '#2F3542', fontSize: '0.86rem' }}>
                  Overall verification progress
                </Typography>
                <Typography sx={{ fontWeight: 700, color: '#be210b', fontSize: '0.86rem' }}>
                  {stats.reviewed}/{stats.total} reviewed · {stats.progress}%
                </Typography>
              </Box>
              <LinearProgress
                variant='determinate'
                value={stats.progress}
                sx={{
                  height: 10,
                  borderRadius: 99,
                  backgroundColor: '#f3e8e6',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 99,
                    background: 'linear-gradient(90deg, #be210b 0%, #f04438 100%)',
                  },
                }}
              />
            </Box>
          </Paper>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
              <CircularProgress size={30} sx={{ color: '#be210b' }} />
            </Box>
          ) : (
            <>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: 'repeat(2, minmax(0, 1fr))',
                    md: 'repeat(3, minmax(0, 1fr))',
                    lg: 'repeat(6, minmax(0, 1fr))',
                  },
                  gap: 1.25,
                }}
              >
                <MetricTile
                  label='Total items'
                  value={stats.total}
                  tone={{
                    bg: '#fff8f7',
                    border: '#f0d5d1',
                    color: '#be210b',
                    track: '#fce7e4',
                  }}
                />
                <MetricTile
                  label='Verified'
                  value={stats.verified}
                  tone={STATUS_TONE.verified}
                  percent={stats.share(stats.verified)}
                />
                <MetricTile
                  label='Discrepancy'
                  value={stats.discrepancy}
                  tone={STATUS_TONE.discrepancy}
                  percent={stats.share(stats.discrepancy)}
                />
                <MetricTile
                  label='Exceeded'
                  value={stats.exceeded}
                  tone={STATUS_TONE.exceeded}
                  percent={stats.share(stats.exceeded)}
                />
                <MetricTile
                  label='Pending'
                  value={stats.pending}
                  tone={STATUS_TONE.pending}
                  percent={stats.share(stats.pending)}
                />
                <MetricTile
                  label='Locations ready'
                  value={`${uploadedCount}/${uploadRows.length || 0}`}
                  tone={{
                    bg: '#eff8ff',
                    border: '#b2ddff',
                    color: '#175cd3',
                    track: '#d1e9ff',
                  }}
                />
              </Box>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', lg: '1.05fr 0.95fr' },
                  gap: 1.5,
                }}
              >
                <SectionCard
                  title='Live activity'
                  subtitle='Changes detected while auto-refresh is running'
                  action={<Chip size='small' label={`${activity.length} events`} sx={{ fontWeight: 700 }} />}
                  bodySx={{ maxHeight: 420, overflowY: 'auto', p: 1.5 }}
                >
                  {activity.length === 0 ? (
                    <Box
                      sx={{
                        border: '1px dashed #efc4bd',
                        borderRadius: '12px',
                        p: 3,
                        textAlign: 'center',
                        backgroundColor: '#fffbfa',
                      }}
                    >
                      <Typography sx={{ fontWeight: 700, color: '#2F3542', mb: 0.5 }}>
                        Watching for changes
                      </Typography>
                      <Typography sx={{ color: '#667085', fontSize: '0.84rem' }}>
                        Adds, deletes, status updates, and edits will appear here as they happen.
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {activity.map((event, index) => {
                        const meta = ACTIVITY_META[event.type] || ACTIVITY_META.modified;
                        const Icon = meta.icon;
                        return (
                          <Box
                            key={event.id}
                            sx={{
                              display: 'grid',
                              gridTemplateColumns: '28px 1fr',
                              gap: 1.1,
                            }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                              }}
                            >
                              <Box
                                sx={{
                                  width: 28,
                                  height: 28,
                                  borderRadius: '8px',
                                  display: 'grid',
                                  placeItems: 'center',
                                  backgroundColor: meta.bg,
                                  color: meta.color,
                                  border: `1px solid ${meta.border}`,
                                }}
                              >
                                <Icon sx={{ fontSize: 16 }} />
                              </Box>
                              {index < activity.length - 1 ? (
                                <Box
                                  sx={{
                                    flex: 1,
                                    width: 2,
                                    mt: 0.5,
                                    mb: -0.5,
                                    minHeight: 18,
                                    backgroundColor: '#f3e8e6',
                                    borderRadius: 99,
                                  }}
                                />
                              ) : null}
                            </Box>
                            <Box
                              sx={{
                                mb: 0.4,
                                p: 1.25,
                                borderRadius: '12px',
                                border: `1px solid ${meta.border}`,
                                backgroundColor: meta.bg,
                              }}
                            >
                              <Box
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  gap: 1,
                                  mb: 0.35,
                                  flexWrap: 'wrap',
                                }}
                              >
                                <Chip
                                  size='small'
                                  label={meta.label}
                                  sx={{
                                    height: 22,
                                    fontWeight: 700,
                                    backgroundColor: '#fff',
                                    color: meta.color,
                                    border: `1px solid ${meta.border}`,
                                  }}
                                />
                                <Typography sx={{ fontSize: '0.72rem', color: '#667085', fontWeight: 600 }}>
                                  {new Date(event.at).toLocaleTimeString()}
                                </Typography>
                              </Box>
                              <Typography sx={{ fontSize: '0.86rem', color: '#101828', fontWeight: 700 }}>
                                {event.detail}
                              </Typography>
                              <Typography sx={{ fontSize: '0.75rem', color: '#667085', mt: 0.25 }}>
                                {event.meta}
                              </Typography>
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </SectionCard>

                <SectionCard
                  title='Upload status'
                  subtitle='Item coverage by location'
                  action={
                    <Chip
                      size='small'
                      label={`${uploadedCount} ready`}
                      sx={{
                        fontWeight: 700,
                        backgroundColor: '#ecfdf3',
                        color: '#067647',
                        border: '1px solid #abefc6',
                      }}
                    />
                  }
                  bodySx={{ maxHeight: 420, overflowY: 'auto', p: 1.5 }}
                >
                  {uploadRows.length === 0 ? (
                    <Typography sx={{ color: '#667085', fontSize: '0.85rem', p: 1 }}>
                      No uploaded locations yet.
                    </Typography>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.1 }}>
                      {uploadRows.map((row) => (
                        <Box
                          key={row.location}
                          sx={{
                            p: 1.35,
                            borderRadius: '12px',
                            border: '1px solid #f3e8e6',
                            backgroundColor: '#fffbfa',
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              gap: 1,
                              mb: 0.9,
                              alignItems: 'center',
                            }}
                          >
                            <Typography sx={{ fontWeight: 700, color: '#2F3542', fontSize: '0.86rem' }}>
                              {row.location}
                            </Typography>
                            <Chip
                              size='small'
                              label={row.status}
                              sx={{
                                height: 22,
                                fontWeight: 700,
                                backgroundColor:
                                  row.status === 'Uploaded' ? '#ecfdf3' : '#fef3f2',
                                color: row.status === 'Uploaded' ? '#067647' : '#b42318',
                              }}
                            />
                          </Box>
                          <LinearProgress
                            variant='determinate'
                            value={row.fill}
                            sx={{
                              height: 7,
                              borderRadius: 99,
                              backgroundColor: '#f3e8e6',
                              '& .MuiLinearProgress-bar': {
                                borderRadius: 99,
                                backgroundColor:
                                  row.status === 'Uploaded' ? '#12b76a' : '#f04438',
                              },
                            }}
                          />
                          <Typography
                            sx={{ mt: 0.7, fontSize: '0.75rem', color: '#667085', fontWeight: 600 }}
                          >
                            {row.itemCount} item{row.itemCount === 1 ? '' : 's'}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                </SectionCard>
              </Box>

              <SectionCard
                title='Modification tracking'
                subtitle='Who changed what, and current status'
                action={
                  <Chip
                    size='small'
                    label={`${recentModifications.length} records`}
                    sx={{
                      fontWeight: 700,
                      backgroundColor: '#fff',
                      border: '1px solid #efc4bd',
                      color: '#be210b',
                    }}
                  />
                }
              >
                {recentModifications.length === 0 ? (
                  <Box
                    sx={{
                      m: 2,
                      border: '1px dashed #efc4bd',
                      borderRadius: '12px',
                      p: 3.5,
                      textAlign: 'center',
                      backgroundColor: '#fffbfa',
                    }}
                  >
                    <Typography sx={{ fontWeight: 700, color: '#2F3542', mb: 0.5 }}>
                      No modifications yet
                    </Typography>
                    <Typography sx={{ color: '#667085', fontSize: '0.84rem' }}>
                      Reviewed items will show here with status, updater, and remarks.
                    </Typography>
                  </Box>
                ) : isMobile ? (
                  <Box sx={{ p: 1.25, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {recentModifications.map((item) => {
                      const tone = STATUS_TONE[item.status] || STATUS_TONE.pending;
                      const updatedAt = timeLabel(item.updatedAt);
                      const sectionLabel = [item.sectionCode, item.sectionTitle]
                        .filter(Boolean)
                        .join(' · ');
                      return (
                        <Box
                          key={item.id}
                          sx={{
                            p: 1.35,
                            borderRadius: '12px',
                            border: '1px solid #f0d5d1',
                            backgroundColor: '#fff',
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              gap: 1,
                              mb: 0.8,
                              alignItems: 'flex-start',
                            }}
                          >
                            <Typography
                              sx={{
                                fontWeight: 700,
                                color: '#101828',
                                fontSize: '0.9rem',
                                textTransform: 'none',
                                lineHeight: 1.35,
                              }}
                            >
                              {item.particulars || 'Untitled item'}
                            </Typography>
                            <Chip
                              size='small'
                              label={tone.label}
                              sx={{
                                height: 24,
                                fontWeight: 700,
                                textTransform: 'none',
                                backgroundColor: tone.bg,
                                color: tone.color,
                                border: `1px solid ${tone.border}`,
                                flexShrink: 0,
                              }}
                            />
                          </Box>
                          <Typography sx={{ color: '#667085', fontSize: '0.78rem', textTransform: 'none' }}>
                            {item.location}
                            {sectionLabel ? ` · ${sectionLabel}` : ''}
                          </Typography>
                          <Typography sx={{ color: '#475467', fontSize: '0.8rem', mt: 0.7, textTransform: 'none' }}>
                            Updated by: {item.statusUpdatedBy || 'Not set'}
                          </Typography>
                          <Typography sx={{ color: '#667085', fontSize: '0.78rem', mt: 0.25, textTransform: 'none' }}>
                            Updated at: {updatedAt || 'Not set'}
                          </Typography>
                          <Typography sx={{ color: '#475467', fontSize: '0.8rem', mt: 0.45, textTransform: 'none' }}>
                            Remarks: {item.remarks || 'Not set'}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                ) : (
                  <TableContainer sx={{ maxHeight: 520 }}>
                    <Table stickyHeader sx={{ minWidth: 860 }}>
                      <TableHead>
                        <TableRow>
                          {[
                            { label: 'Item', width: '24%' },
                            { label: 'Location', width: '16%' },
                            { label: 'Section', width: '16%' },
                            { label: 'Status', width: '12%' },
                            { label: 'Updated by', width: '12%' },
                            { label: 'Updated at', width: '12%' },
                            { label: 'Remarks', width: '18%' },
                          ].map((column) => (
                            <TableCell
                              key={column.label}
                              sx={{
                                width: column.width,
                                backgroundColor: '#fff7f6',
                                color: '#667085',
                                fontWeight: 700,
                                fontSize: '0.72rem',
                                letterSpacing: '0.04em',
                                textTransform: 'uppercase',
                                whiteSpace: 'nowrap',
                                borderBottom: '1px solid #f0d5d1',
                                py: 1.5,
                                px: 2,
                              }}
                            >
                              {column.label?.toUpperCase()}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {recentModifications.map((item, index) => {
                          const tone = STATUS_TONE[item.status] || STATUS_TONE.pending;
                          const updatedAt = timeLabel(item.updatedAt);
                          const sectionLabel = [item.sectionCode, item.sectionTitle]
                            .filter(Boolean)
                            .join(' · ');
                          return (
                            <TableRow
                              key={item.id}
                              hover
                              sx={{
                                backgroundColor: index % 2 === 0 ? '#fff' : '#fffbfa',
                                '&:hover': { backgroundColor: '#fff1ef' },
                                '& td': {
                                  borderBottom: '1px solid #f3e8e6',
                                  py: 1.75,
                                  px: 2,
                                  verticalAlign: 'middle',
                                  textTransform: 'none',
                                },
                              }}
                            >
                              <TableCell>
                                <Typography
                                  sx={{
                                    fontWeight: 700,
                                    color: '#101828',
                                    fontSize: '0.9rem',
                                    lineHeight: 1.35,
                                    textTransform: 'none',
                                  }}
                                >
                                  {item.particulars || 'Untitled item'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography
                                  sx={{
                                    color: '#344054',
                                    fontSize: '0.84rem',
                                    fontWeight: 600,
                                    textTransform: 'none',
                                  }}
                                >
                                  {item.location || <EmptyValue />}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography
                                  sx={{
                                    color: '#475467',
                                    fontSize: '0.84rem',
                                    textTransform: 'none',
                                  }}
                                >
                                  {sectionLabel || <EmptyValue />}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  size='small'
                                  label={tone.label}
                                  sx={{
                                    height: 26,
                                    fontWeight: 700,
                                    fontSize: '0.74rem',
                                    textTransform: 'none',
                                    backgroundColor: tone.bg,
                                    color: tone.color,
                                    border: `1px solid ${tone.border}`,
                                  }}
                                />
                              </TableCell>
                              <TableCell>
                                {item.statusUpdatedBy ? (
                                  <Typography
                                    sx={{
                                      fontWeight: 600,
                                      color: '#2F3542',
                                      fontSize: '0.84rem',
                                      textTransform: 'none',
                                    }}
                                  >
                                    {item.statusUpdatedBy}
                                  </Typography>
                                ) : (
                                  <EmptyValue />
                                )}
                              </TableCell>
                              <TableCell>
                                {updatedAt ? (
                                  <Typography
                                    sx={{
                                      color: '#667085',
                                      fontSize: '0.8rem',
                                      textTransform: 'none',
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    {updatedAt}
                                  </Typography>
                                ) : (
                                  <EmptyValue />
                                )}
                              </TableCell>
                              <TableCell sx={{ maxWidth: 220 }}>
                                {item.remarks ? (
                                  <Typography
                                    sx={{
                                      color: '#475467',
                                      fontSize: '0.84rem',
                                      textTransform: 'none',
                                      lineHeight: 1.4,
                                    }}
                                  >
                                    {item.remarks}
                                  </Typography>
                                ) : (
                                  <EmptyValue />
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </SectionCard>
            </>
          )}
        </Box>
      </div>
    </LayoutContainer>
  );
};

export default StructureMonitor;
